import { useState, useEffect, useCallback, useRef } from 'react';
import { req } from '../utils'; // Assuming utils.js is one level up

// A list of distinct colors for BDMs
const PREDEFINED_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FED766', '#2AB7CA', '#F0B67F', '#7E57C2', 
  '#FF8A65', '#4DB6AC', '#9575CD', '#AED581', '#FFB74D', '#F06292', '#7986CB',
  '#A1887F', '#FFD54F', '#4DD0E1', '#BA68C8', '#81C784', '#FFF176', '#FF80AB',
  '#64B5F6', '#E57373', '#DCE775', '#90A4AE', '#FFAB91', '#B39DDB', '#C5E1A5',
  '#80DEEA', '#F48FB1' // 30 distinct colors
];

export const useBdmData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stores info for ALL BDMs encountered (API-fetched or dynamically added as fallbacks)
  // Key: account_id (string), Value: { account_id, name, email, region, color, isFallback }
  const [bdmInfoMap, setBdmInfoMap] = useState(new Map());
  
  // Ref to store only the BDMs fetched from the API, keyed by accountId.
  // This is useful for functions that should only operate on API-confirmed data.
  const apiBdmDetailsRef = useRef(new Map());
  
  // Ref to keep track of the next color index to assign for new/fallback BDMs
  const nextColorIndex = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Endpoint is 'get_bdm_regions'
        // Expected structure: [{ account_id: '1', name: 'John Doe', email: 'j.doe@example.com', region: 'NSW' }, ...]
        const response = await req('get', 'get_bdm_regions');
        
        const initialApiBdms = new Map();
        let colorIdx = 0;

        if (Array.isArray(response)) {
          response.forEach(bdm => {
            if (bdm.account_id === undefined || bdm.account_id === null) {
              console.warn('BDM data from API missing account_id:', bdm);
              return; // Skip BDMs without an account_id
            }
            const id = String(bdm.account_id);
            // Only add if not already processed (safeguard, though API should send unique)
            if (!initialApiBdms.has(id)) {
              initialApiBdms.set(id, {
                ...bdm,
                account_id: id, // Ensure account_id is a string
                color: PREDEFINED_COLORS[colorIdx % PREDEFINED_COLORS.length],
                isFallback: false
              });
              colorIdx++;
            }
          });
        } else {
          console.error("API response for get_bdm_regions is not an array:", response);
          throw new Error("Invalid API response structure for BDMs");
        }
        
        apiBdmDetailsRef.current = new Map(initialApiBdms);
        setBdmInfoMap(new Map(initialApiBdms)); // Initialize bdmInfoMap with API data
        nextColorIndex.current = colorIdx; // Set nextColorIndex after processing API data
        setError(null);
      } catch (err) {
        console.error("Failed to fetch BDM data:", err);
        setError(err);
        apiBdmDetailsRef.current.clear();
        setBdmInfoMap(new Map()); // Clear map on error
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Fetch once on mount

  const getBdmInfo = useCallback((accountIdInput, defaultName = 'Unknown BDM', defaultEmail = '') => {
    if (accountIdInput === undefined || accountIdInput === null) {
      // For truly unknown/invalid IDs, provide a generic fallback. 
      // To ensure even these get unique colors if multiple such calls occur, 
      // we could use a unique symbol or a counter for the key if not storing them.
      // For simplicity here, we assign a color from the sequence but don't add to the permanent map
      // unless we decide all fallbacks (even those without a provided ID) should be memoized.
      // Given the request for consistency, let's try to assign a unique color even for null IDs if they need to be displayed.
      // However, it's better if the calling code provides some unique fallback ID if possible.
      // For now, returning a temporary object with a color.
      const tempFallbackColor = PREDEFINED_COLORS[nextColorIndex.current % PREDEFINED_COLORS.length]; // Don't increment ref here for non-stored
      return {
        account_id: `fallback_${Date.now()}`, // Temporary unique ID
        name: defaultName,
        email: defaultEmail,
        region: null,
        color: tempFallbackColor,
        isFallback: true
      };
    }
    
    const accountId = String(accountIdInput);

    if (bdmInfoMap.has(accountId)) {
      return bdmInfoMap.get(accountId);
    }

    // BDM not found in current map (it's a new fallback ID)
    // Assign a new color and add it. This makes it consistent for subsequent calls for *this specific accountId*.
    const newColor = PREDEFINED_COLORS[nextColorIndex.current % PREDEFINED_COLORS.length];
    const newBdmEntry = {
      account_id: accountId,
      name: defaultName, 
      email: defaultEmail,
      region: null, // Fallbacks typically don't have a region from this call
      color: newColor,
      isFallback: true 
    };
    
    // Update the bdmInfoMap state to include this new fallback BDM
    setBdmInfoMap(prevMap => {
        const updatedMap = new Map(prevMap);
        updatedMap.set(accountId, newBdmEntry);
        return updatedMap;
    });
    nextColorIndex.current += 1; // Increment color index only when a new BDM is actually added to the map
    
    return newBdmEntry;
  }, [bdmInfoMap]); // bdmInfoMap is a dependency

  const getFetchedBdmDetailsList = useCallback(() => {
    // Returns BDMs that were confirmed by the API
    return Array.from(apiBdmDetailsRef.current.values());
  }, []); // Depends only on the ref which is stable after initial load

  const getAllKnownBdmDetailsList = useCallback(() => {
    // Returns all BDMs, including API-fetched and dynamically added fallbacks
    return Array.from(bdmInfoMap.values());
  }, [bdmInfoMap]);


  const getBdmByRegion = useCallback((regionName) => {
    if (!regionName) return null;
    const lowerRegionName = regionName.toLowerCase();
    // Search within API-confirmed BDMs for region matching
    for (const bdm of apiBdmDetailsRef.current.values()) {
      if (bdm.region && String(bdm.region).toLowerCase() === lowerRegionName) {
        // Return the full BDM object including name, email, and color
        return bdm; 
      }
    }
    return null; // No BDM found for that region in the API data
  }, []); // Depends only on the ref

  return { 
    loading, 
    error,
    getBdmInfo, 
    getFetchedBdmDetailsList,
    getAllKnownBdmDetailsList,
    getBdmByRegion,
    bdmInfoMap, // Exposing the map for advanced use cases or context provider
  };
};

/*
Example Usage:

import { useBdmData } from './hooks/useBdmData'; // Adjust path as needed

function MyComponent({ specificBdmId, targetRegion }) {
  const { 
    loading, 
    error, 
    getBdmInfo, 
    getFetchedBdmDetailsList, 
    getBdmByRegion 
  } = useBdmData();

  if (loading) return <p>Loading BDM data...</p>;
  if (error) return <p>Error loading BDM data: {error.message}</p>;

  const specificBdm = getBdmInfo(specificBdmId, 'Default Name for Fallback');
  const bdmForTargetRegion = getBdmByRegion(targetRegion);
  const allApiBdms = getFetchedBdmDetailsList();

  return (
    <div>
      {specificBdm && (
        <p style={{ color: specificBdm.color }}>
          BDM ({specificBdm.account_id}): {specificBdm.name} ({specificBdm.email || 'N/A'})
          {specificBdm.isFallback && <span> (This is a fallback BDM entry)</span>}
        </p>
      )}

      {bdmForTargetRegion ? (
        <p style={{ color: bdmForTargetRegion.color }}>
          Manager for {bdmForTargetRegion.region}: {bdmForTargetRegion.name} ({bdmForTargetRegion.email})
        </p>
      ) : (
        <p>No BDM assigned to region: {targetRegion}</p>
      )}

      <h3>All BDMs (from API):</h3>
      <ul>
        {allApiBdms.map(bdm => (
          <li key={bdm.account_id} style={{ color: bdm.color }}>
            {bdm.name} ({bdm.email}) - Region: {bdm.region || 'N/A'}
          </li>
        ))}
      </ul>
    </div>
  );
}
*/ 